import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  InputSource,
  Interview,
  InterviewMode,
  SourceType,
  FacetCategory,
  InterviewTypeId,
  FacetMappingDecision,
} from '@/types/interviewer'
import { sourceTypeToMode } from '@/types/interviewer'
import { analyzeContent } from '@/lib/interviewer/analyzer'
import { update as sparqlUpdate } from '@/lib/api/sparql-adapter'
import {
  createEntitySparql,
  validateDecisionForCreation,
} from '@/lib/interviewer/entityCreation'
import { debugLog } from '@/lib/debugLogger'
import { useProjectStore } from './projectStore'
import { useAuthStore } from './authStore'
import { sourcesApi } from '@/lib/api/client'
import type { Source } from '@ispot/shared/types'

// Workflow step for the generic interviewer
export type InterviewWorkflowStep = 'setup' | 'analyzing' | 'detection' | 'mapping' | 'complete'

interface InterviewerStore {
  // State
  sources: InputSource[]
  currentSource: InputSource | null
  isAnalyzing: boolean
  selectedFacetId: string | null
  filterSourceType: SourceType | 'all'
  filterCategory: FacetCategory | 'all'
  searchQuery: string

  // Workflow state for new interview creation
  workflowStep: InterviewWorkflowStep
  pendingMappingDecisions: FacetMappingDecision[]
  isApplyingMappingDecisions: boolean // Lock to prevent race condition

  // Actions - New unified API
  createSource: (sourceType: SourceType, title: string, content: string, options?: {
    context?: string
    sourceUrl?: string
    sourceRef?: string
    interviewee?: string
    interviewTypeId?: InterviewTypeId
    linkedPersonId?: string
    linkedPersonName?: string
  }) => InputSource
  updateSource: (id: string, updates: Partial<InputSource>) => void
  deleteSource: (id: string) => void
  hydrateSources: (projectId: string) => Promise<void>
  setCurrentSource: (source: InputSource | null) => void
  clearCurrentSource: () => void
  analyzeSource: (id: string) => Promise<void>
  dismissFacet: (sourceId: string, facetId: string, reason: string) => void
  restoreFacet: (sourceId: string, facetId: string) => void
  setFilterSourceType: (sourceType: SourceType | 'all') => void
  setFilterCategory: (category: FacetCategory | 'all') => void
  setSearchQuery: (query: string) => void

  // Workflow actions for generic interviewer
  setWorkflowStep: (step: InterviewWorkflowStep) => void
  addMappingDecision: (decision: FacetMappingDecision) => void
  updateMappingDecision: (facetId: string, updates: Partial<FacetMappingDecision>) => void
  removeMappingDecision: (facetId: string) => void
  clearMappingDecisions: () => void
  applyMappingDecisions: (sourceId: string) => Promise<void>
  resetWorkflow: () => void

  // Legacy aliases for backward compatibility
  createInterview: (mode: InterviewMode, title: string, content: string, context?: string) => Interview
  updateInterview: (id: string, updates: Partial<Interview>) => void
  deleteInterview: (id: string) => void
  setCurrentInterview: (interview: Interview | null) => void
  clearCurrentInterview: () => void
  analyzeInterview: (id: string) => Promise<void>
  setFilterMode: (mode: InterviewMode | 'all') => void

  // Computed getters (as functions)
  getInterviews: () => Interview[]
  getCurrentInterview: () => Interview | null
  getFilterMode: () => InterviewMode | 'all'
}

const generateId = () => `src_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

// ============================================================================
// TYPE CONVERSION HELPERS (Frontend ↔ Backend)
// ============================================================================

/**
 * Convert frontend InputSource to backend Source format for API calls
 * Maps frontend-specific fields to backend schema
 */
function inputSourceToBackendSource(
  source: Partial<InputSource>,
  accountId: string,
  projectId: string
): {
  title: string
  contentType: string
  rawContent: string
  markdownContent?: string
  tags?: string[]
  metadata?: Record<string, unknown>
} {
  return {
    title: source.title || 'Untitled',
    contentType: source.sourceType || 'generic',
    rawContent: source.rawContent || '',
    markdownContent: source.rawContent || '', // Use rawContent as markdown for interviews
    tags: source.tags || [],
    metadata: {
      // Preserve interview-specific context
      createdInInterviewer: true,
      interviewTypeId: source.interviewTypeId,
      interviewee: source.interviewee,
      context: source.context,
      linkedPersonId: source.linkedPersonId,
      linkedPersonName: source.linkedPersonName,
      sourceUrl: source.sourceUrl,
      sourceRef: source.sourceRef,
      // Track original temp ID for debugging
      originalTempId: source.id,
    },
  }
}

/**
 * Convert backend Source to frontend InputSource format
 * Extracts interview-specific fields from metadata
 */
function backendSourceToInputSource(dbSource: Source): InputSource {
  const metadata = (dbSource.metadata || {}) as {
    interviewTypeId?: string
    interviewee?: string
    context?: string
    linkedPersonId?: string
    linkedPersonName?: string
    sourceUrl?: string
    sourceRef?: string
  }

  return {
    id: dbSource.id,
    sourceType: dbSource.contentType as SourceType,
    interviewTypeId: metadata.interviewTypeId,
    title: dbSource.title,

    // Source context
    interviewee: metadata.interviewee,
    context: metadata.context || '',
    linkedPersonId: metadata.linkedPersonId,
    linkedPersonName: metadata.linkedPersonName,

    // Content
    rawContent: dbSource.rawContent,
    sourceUrl: metadata.sourceUrl,
    sourceRef: metadata.sourceRef,

    // Processing state
    createdAt: dbSource.createdAt || new Date().toISOString(),
    analyzedAt: dbSource.updatedAt,
    status: mapBackendStatusToFrontend(dbSource.status),

    // Extracted data (initialize empty, populated later)
    facets: [],
    scores: {
      discovery: 0,
      decisions: 0,
      alignment: 0,
      overall: 0,
    },
    qualityNotes: [],

    // Organization
    tags: dbSource.tags || [],
    projectId: dbSource.projectId,
  }
}

/**
 * Map backend status to frontend status
 */
function mapBackendStatusToFrontend(
  backendStatus: 'pending' | 'processing' | 'analyzed' | 'failed'
): 'draft' | 'analyzing' | 'analyzed' | 'error' {
  switch (backendStatus) {
    case 'pending':
      return 'draft'
    case 'processing':
      return 'analyzing'
    case 'analyzed':
      return 'analyzed'
    case 'failed':
      return 'error'
    default:
      return 'draft'
  }
}

// Convert legacy InterviewMode to SourceType
function modeToSourceType(mode: InterviewMode): SourceType {
  switch (mode) {
    case 'discovery':
      return 'user_interview'
    case 'decisions':
      return 'meeting_notes'
    case 'alignment':
      return 'stakeholder_interview'
    default:
      return 'generic'
  }
}

// Add mode property to source for legacy compatibility
function sourceToInterview(source: InputSource): Interview {
  return {
    ...source,
    mode: sourceTypeToMode(source.sourceType),
  }
}

export const useInterviewerStore = create<InterviewerStore>()(
  persist(
    (set, get) => ({
      // Initial state
      sources: [],
      currentSource: null,
      isAnalyzing: false,
      selectedFacetId: null,
      filterSourceType: 'all',
      filterCategory: 'all',
      searchQuery: '',
      workflowStep: 'setup' as InterviewWorkflowStep,
      pendingMappingDecisions: [],
      isApplyingMappingDecisions: false,

      // Computed getters as functions
      getInterviews: () => get().sources.map(sourceToInterview),
      getCurrentInterview: () => {
        const current = get().currentSource
        return current ? sourceToInterview(current) : null
      },
      getFilterMode: () => {
        const sourceType = get().filterSourceType
        if (sourceType === 'all') return 'all'
        return sourceTypeToMode(sourceType)
      },

      // Create a new source
      createSource: (sourceType, title, content, options = {}) => {
        const tempId = generateId()
        const source: InputSource = {
          id: tempId,
          sourceType,
          interviewTypeId: options.interviewTypeId,
          title,
          context: options.context || '',
          rawContent: content,
          sourceUrl: options.sourceUrl,
          sourceRef: options.sourceRef,
          interviewee: options.interviewee,
          linkedPersonId: options.linkedPersonId,
          linkedPersonName: options.linkedPersonName,
          createdAt: new Date().toISOString(),
          status: 'draft',
          facets: [],
          scores: {},
          qualityNotes: [],
          mappingDecisions: [],
        }

        // Optimistically add to store immediately
        set((state) => ({
          sources: [...state.sources, source],
          currentSource: source,
          workflowStep: 'setup' as InterviewWorkflowStep,
          pendingMappingDecisions: [],
        }))

        // Persist to database in background
        const currentProject = useProjectStore.getState().currentProject
        const currentAccount = useAuthStore.getState().currentAccount

        if (currentProject && currentAccount) {
          sourcesApi
            .create(
              currentAccount.id,
              currentProject.id,
              inputSourceToBackendSource(source, currentAccount.id, currentProject.id)
            )
            .then((dbSource) => {
              // DON'T update the source ID yet - keep temp ID to prevent re-renders
              // Updating the ID during workflow causes duplicate entity creation
              // The database ID is stored in metadata for later use
              debugLog('Interview persisted to database:', dbSource.id, '(temp ID preserved)')

              // Silently update metadata without triggering component re-renders
              const currentState = get()
              const sourceIndex = currentState.sources.findIndex((s) => s.id === tempId)

              if (sourceIndex !== -1) {
                const updatedSources = [...currentState.sources]
                updatedSources[sourceIndex] = {
                  ...updatedSources[sourceIndex],
                  metadata: {
                    ...updatedSources[sourceIndex].metadata,
                    databaseId: dbSource.id, // Store database UUID here
                    syncedAt: new Date().toISOString(),
                  },
                  createdAt: dbSource.createdAt || source.createdAt,
                }
                // Update sources array without changing currentSource
                set({ sources: updatedSources })
              }
            })
            .catch((error) => {
              console.error('Failed to persist interview to database:', error)
              // Note: Interview remains in localStorage, can retry sync later
            })
        } else {
          console.warn('No project/account context - interview will only be saved to localStorage')
        }

        return source
      },

      // Update a source
      updateSource: (id, updates) => {
        set((state) => ({
          sources: state.sources.map((s) =>
            s.id === id ? { ...s, ...updates } : s
          ),
          currentSource:
            state.currentSource?.id === id
              ? { ...state.currentSource, ...updates }
              : state.currentSource,
        }))

        // Sync update to database (if ID is a UUID, meaning it's already persisted)
        const isPersistedSource = id.includes('-') // UUIDs contain dashes, temp IDs don't
        if (isPersistedSource) {
          const currentProject = useProjectStore.getState().currentProject
          const currentAccount = useAuthStore.getState().currentAccount

          if (currentProject && currentAccount) {
            const source = get().sources.find((s) => s.id === id)
            if (source) {
              sourcesApi
                .update(
                  currentAccount.id,
                  currentProject.id,
                  id,
                  inputSourceToBackendSource({ ...source, ...updates }, currentAccount.id, currentProject.id)
                )
                .catch((error) => {
                  console.error('Failed to sync interview update to database:', error)
                })
            }
          }
        }
      },

      // Delete a source
      deleteSource: (id) => {
        set((state) => ({
          sources: state.sources.filter((s) => s.id !== id),
          currentSource:
            state.currentSource?.id === id ? null : state.currentSource,
        }))

        // Delete from database (if ID is a UUID, meaning it's already persisted)
        const isPersistedSource = id.includes('-') // UUIDs contain dashes, temp IDs don't
        if (isPersistedSource) {
          const currentProject = useProjectStore.getState().currentProject
          const currentAccount = useAuthStore.getState().currentAccount

          if (currentProject && currentAccount) {
            sourcesApi
              .delete(currentAccount.id, currentProject.id, id)
              .catch((error) => {
                console.error('Failed to delete interview from database:', error)
                // Note: Already removed from UI, this is just cleanup
              })
          }
        }
      },

      // Hydrate sources from database (load interviews on app start)
      hydrateSources: async (projectId: string) => {
        const currentAccount = useAuthStore.getState().currentAccount
        if (!currentAccount) {
          console.warn('Cannot hydrate sources: no account context')
          return
        }

        try {
          debugLog('Hydrating interviews from database...')

          // Load all interview sources from database
          const dbSources = await sourcesApi.list(currentAccount.id, projectId, {
            contentType: [
              'user_interview',
              'buyer_interview',
              'stakeholder_interview',
              'meeting_notes',
              'generic',
            ],
          })

          debugLog(`Loaded ${dbSources.length} interviews from database`)

          // Convert to InputSource format
          const inputSources = dbSources.map(backendSourceToInputSource)

          // Merge with existing localStorage sources
          // Database wins on conflict (same ID)
          const existingSources = get().sources
          const tempSources = existingSources.filter((s) => !s.id.includes('-')) // Keep temp IDs only

          // Combine: database sources + temp (unsynced) sources
          const mergedSources = [...inputSources, ...tempSources]

          set({ sources: mergedSources })
          debugLog('Interview hydration complete')
        } catch (error) {
          console.error('Failed to hydrate sources from database:', error)
          // Keep existing localStorage sources on error
        }
      },

      // Set current source
      setCurrentSource: (source) => {
        set({ currentSource: source })
      },

      // Clear current source
      clearCurrentSource: () => {
        set({ currentSource: null, selectedFacetId: null })
      },

      // Analyze source with AI
      analyzeSource: async (id) => {
        const source = get().sources.find((s) => s.id === id)
        if (!source) {
          console.error('Source not found:', id)
          return
        }

        set({ isAnalyzing: true })
        get().updateSource(id, { status: 'analyzing' })

        // Get project context for optimized backend extraction
        const currentProject = useProjectStore.getState().currentProject
        const currentAccount = useAuthStore.getState().currentAccount

        try {
          const result = await analyzeContent({
            sourceId: id,
            sourceType: source.sourceType,
            content: source.rawContent,
            context: source.context,
            sourceUrl: source.sourceUrl,
            projectId: currentProject?.id,
            accountId: currentAccount?.id,
          })

          get().updateSource(id, {
            status: 'analyzed',
            analyzedAt: new Date().toISOString(),
            facets: result.facets,
            scores: result.scores,
            qualityNotes: result.qualityNotes,
          })
        } catch (error) {
          console.error('Analysis failed:', error)
          get().updateSource(id, {
            status: 'error',
            errorMessage: error instanceof Error ? error.message : 'Analysis failed',
          })
        } finally {
          set({ isAnalyzing: false })
        }
      },

      // Dismiss a facet
      dismissFacet: (sourceId, facetId, reason) => {
        set((state) => ({
          sources: state.sources.map((source) =>
            source.id === sourceId
              ? {
                  ...source,
                  facets: source.facets.map((f) =>
                    f.id === facetId
                      ? { ...f, dismissed: true, dismissedReason: reason }
                      : f
                  ),
                }
              : source
          ),
          currentSource:
            state.currentSource?.id === sourceId
              ? {
                  ...state.currentSource,
                  facets: state.currentSource.facets.map((f) =>
                    f.id === facetId
                      ? { ...f, dismissed: true, dismissedReason: reason }
                      : f
                  ),
                }
              : state.currentSource,
        }))
      },

      // Restore a dismissed facet
      restoreFacet: (sourceId, facetId) => {
        set((state) => ({
          sources: state.sources.map((source) =>
            source.id === sourceId
              ? {
                  ...source,
                  facets: source.facets.map((f) =>
                    f.id === facetId
                      ? { ...f, dismissed: false, dismissedReason: undefined }
                      : f
                  ),
                }
              : source
          ),
          currentSource:
            state.currentSource?.id === sourceId
              ? {
                  ...state.currentSource,
                  facets: state.currentSource.facets.map((f) =>
                    f.id === facetId
                      ? { ...f, dismissed: false, dismissedReason: undefined }
                      : f
                  ),
                }
              : state.currentSource,
        }))
      },

      // Set filter source type
      setFilterSourceType: (sourceType) => {
        set({ filterSourceType: sourceType })
      },

      // Set filter category
      setFilterCategory: (category) => {
        set({ filterCategory: category })
      },

      // Set search query
      setSearchQuery: (query) => {
        set({ searchQuery: query })
      },

      // ============================================
      // Workflow actions for generic interviewer
      // ============================================

      setWorkflowStep: (step) => {
        set({ workflowStep: step })
      },

      addMappingDecision: (decision) => {
        set((state) => ({
          pendingMappingDecisions: [
            ...state.pendingMappingDecisions.filter(d => d.facetId !== decision.facetId),
            decision,
          ],
        }))
      },

      updateMappingDecision: (facetId, updates) => {
        set((state) => ({
          pendingMappingDecisions: state.pendingMappingDecisions.map((d) =>
            d.facetId === facetId ? { ...d, ...updates } : d
          ),
        }))
      },

      removeMappingDecision: (facetId) => {
        set((state) => ({
          pendingMappingDecisions: state.pendingMappingDecisions.filter(d => d.facetId !== facetId),
        }))
      },

      clearMappingDecisions: () => {
        set({ pendingMappingDecisions: [] })
      },

      applyMappingDecisions: async (sourceId) => {
        // LOCK: Prevent concurrent execution (race condition protection)
        if (get().isApplyingMappingDecisions) {
          debugLog('applyMappingDecisions', '⚠️ BLOCKED - Already executing, preventing race condition')
          return
        }

        // Set lock flag
        set({ isApplyingMappingDecisions: true })

        try {
          // Immediately get and clear decisions to prevent double-execution
          const decisions = get().pendingMappingDecisions

          // Guard: if no decisions, nothing to do (prevents double-execution)
          if (decisions.length === 0) {
            debugLog('applyMappingDecisions', 'No decisions to apply - returning early')
            return
          }

          // Clear decisions immediately to prevent re-execution
          set({ pendingMappingDecisions: [] })

          const source = get().sources.find(s => s.id === sourceId)
          debugLog('applyMappingDecisions', '========================================')
          debugLog('applyMappingDecisions', `Processing ${decisions.length} mapping decisions for source ${sourceId}`)
          debugLog('applyMappingDecisions', `Source has ${source?.facets?.length || 0} facets`)

          // Create entities for decisions with 'create_new' or 'accept_suggestion' action
          const updatedDecisions: FacetMappingDecision[] = []
          let createdCount = 0
          let skippedCount = 0
          let errorCount = 0
          const skippedReasons: string[] = []

          for (let i = 0; i < decisions.length; i++) {
            const decision = decisions[i]
            let updatedDecision = { ...decision }

            // Get facet content for label fallback
            const facet = source?.facets.find(f => f.id === decision.facetId)
            const facetContent = facet?.content

            // Validate decision using unit-tested utility
            const validation = validateDecisionForCreation(decision, facetContent)

            debugLog('applyMappingDecisions', `[${i + 1}/${decisions.length}] Facet ${decision.facetId}`, {
              action: decision.action,
              isValid: validation.isValid,
              entityType: validation.entityType,
              entityLabel: validation.entityLabel?.slice(0, 40),
              reason: validation.reason || 'OK',
            })

            if (!validation.isValid) {
              skippedCount++
              skippedReasons.push(`Facet ${decision.facetId}: ${validation.reason}`)
              updatedDecisions.push(updatedDecision)
              continue
            }

            // Validation passed - create the entity
            const entityType = validation.entityType!
            const entityLabel = validation.entityLabel!

            try {
              // Use facet evidence or content for description
              const description = facet?.evidence || facet?.content

              // Create the entity in Fuseki
              const { sparql, uri } = createEntitySparql(entityType, entityLabel, description)

              debugLog('applyMappingDecisions', `Creating ${entityType}: "${entityLabel.slice(0, 50)}..."`)

              await sparqlUpdate(sparql, false)

              debugLog('applyMappingDecisions', `Created successfully: ${uri}`)

              // Update the decision with the created entity URI
              updatedDecision = {
                ...decision,
                linkedEntityUri: uri,
                linkedEntityType: entityType,
                linkedEntityLabel: entityLabel,
              }

              createdCount++
            } catch (error) {
              errorCount++
              const errorMsg = error instanceof Error ? error.message : String(error)
              debugLog('applyMappingDecisions', `SPARQL ERROR for ${entityType}: ${errorMsg}`)
              skippedReasons.push(`Facet ${decision.facetId}: SPARQL error - ${errorMsg}`)
            }

            updatedDecisions.push(updatedDecision)
          }

          debugLog('applyMappingDecisions', '========================================')
          debugLog('applyMappingDecisions', 'SUMMARY', {
            total: decisions.length,
            created: createdCount,
            skipped: skippedCount,
            errors: errorCount,
          })

          if (skippedReasons.length > 0) {
            debugLog('applyMappingDecisions', 'SKIPPED REASONS', skippedReasons)
          }

          // Update source with mapping decisions
          get().updateSource(sourceId, {
            mappingDecisions: updatedDecisions,
            mappingComplete: true,
            status: 'analyzed',
          })

          // Move to complete step (pendingMappingDecisions already cleared at start)
          set({ workflowStep: 'complete' as InterviewWorkflowStep })
        } finally {
          // Always release lock, even if error occurred
          set({ isApplyingMappingDecisions: false })
          debugLog('applyMappingDecisions', '🔓 Lock released')
        }
      },

      resetWorkflow: () => {
        set({
          workflowStep: 'setup' as InterviewWorkflowStep,
          pendingMappingDecisions: [],
        })
      },

      // ============================================
      // Legacy API for backward compatibility
      // ============================================

      createInterview: (mode, title, content, context) => {
        const sourceType = modeToSourceType(mode)
        const source = get().createSource(sourceType, title, content, { context })
        return sourceToInterview(source)
      },

      updateInterview: (id, updates) => {
        get().updateSource(id, updates as Partial<InputSource>)
      },

      deleteInterview: (id) => {
        get().deleteSource(id)
      },

      setCurrentInterview: (interview) => {
        if (interview) {
          // Find the actual source
          const source = get().sources.find(s => s.id === interview.id)
          set({ currentSource: source || interview as unknown as InputSource })
        } else {
          set({ currentSource: null })
        }
      },

      clearCurrentInterview: () => {
        get().clearCurrentSource()
      },

      analyzeInterview: async (id) => {
        await get().analyzeSource(id)
      },

      setFilterMode: (mode) => {
        if (mode === 'all') {
          set({ filterSourceType: 'all' })
        } else {
          set({ filterSourceType: modeToSourceType(mode) })
        }
      },
    }),
    {
      name: 'intellispot-interviewer',
      partialize: (state) => ({
        sources: state.sources,
        filterSourceType: state.filterSourceType,
        filterCategory: state.filterCategory,
      }),
      // Migration from old interviews to sources
      migrate: (persistedState: unknown, version: number) => {
        const state = persistedState as Record<string, unknown>
        if (version === 0 && state.interviews && !state.sources) {
          // Migrate interviews to sources
          const interviews = state.interviews as Interview[]
          const sources: InputSource[] = interviews.map((interview) => ({
            ...interview,
            sourceType: modeToSourceType(interview.mode),
          }))
          return {
            ...state,
            sources,
            interviews: undefined,
            filterSourceType: state.filterMode || 'all',
          }
        }
        return state
      },
      version: 1,
    }
  )
)

// Selector hooks for computed values
export const useInterviews = () => useInterviewerStore((state) => state.sources.map(sourceToInterview))
export const useCurrentInterview = () => {
  const currentSource = useInterviewerStore((state) => state.currentSource)
  return currentSource ? sourceToInterview(currentSource) : null
}
